import { create } from 'zustand'
import { authApi } from '@/api/endpoints/auth'
import { setAuthTokens, clearAuthTokens, getAccessToken, getRefreshToken } from '@/api/client'
import type { User, AuthTokens, LoginCredentials } from '@/types'

interface AuthState {
  user: User | null
  tokens: AuthTokens | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null

  // Actions
  setUser: (user: User | null) => void
  setTokens: (tokens: AuthTokens | null) => void
  setLoading: (loading: boolean) => void
  login: (credentials: LoginCredentials) => Promise<void>
  logout: () => Promise<void>
  refreshToken: () => Promise<boolean>
  loadFromStorage: () => Promise<void>
  updateUser: (updates: Partial<User>) => void
  clearError: () => void
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,

  setUser: (user) => {
    set({ user, isAuthenticated: user !== null })
  },

  setTokens: (tokens) => {
    set({ tokens })
    if (tokens) {
      setAuthTokens(tokens.accessToken, tokens.refreshToken)
    } else {
      clearAuthTokens()
    }
  },

  setLoading: (isLoading) => {
    set({ isLoading })
  },

  login: async (credentials: LoginCredentials) => {
    set({ isLoading: true, error: null })
    try {
      const { user, tokens } = await authApi.login(credentials)

      setAuthTokens(tokens.accessToken, tokens.refreshToken, user.tenantId)

      set({
        user,
        tokens,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      })
    } catch (error: unknown) {
      const message =
        error && typeof error === 'object' && 'message' in error
          ? String((error as { message: string }).message)
          : 'Login failed. Please check your credentials.'

      set({ isLoading: false, error: message, isAuthenticated: false })
      throw error
    }
  },

  logout: async () => {
    set({ isLoading: true })
    try {
      await authApi.logout()
    } catch {
      // Ignore errors on logout - we clear state regardless
    } finally {
      clearAuthTokens()
      set({
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      })
    }
  },

  refreshToken: async (): Promise<boolean> => {
    try {
      // Cookie is sent automatically via withCredentials; only pass body token if present
      const storedRefresh = getRefreshToken()
      const tokens = await authApi.refresh(storedRefresh || undefined)
      setAuthTokens(tokens.accessToken, tokens.refreshToken)
      set({ tokens })
      return true
    } catch {
      // Don't call logout() here — that would fire an API call and potentially
      // cause redirect loops. Let the caller handle clearing auth state.
      return false
    }
  },

  loadFromStorage: async () => {
    const accessToken = getAccessToken()
    const refreshToken = getRefreshToken()

    // No localStorage tokens at all — skip API calls (not logged in, no cookie to check)
    if (!accessToken && !refreshToken) {
      set({ isLoading: false })
      return
    }

    set({ isLoading: true })
    try {
      // httpOnly cookie + localStorage token (via Authorization header) both attempted
      const user = await authApi.getMe()
      set({
        user,
        isAuthenticated: true,
        isLoading: false,
        tokens: accessToken
          ? { accessToken, refreshToken: refreshToken || '', expiresIn: 3600, tokenType: 'Bearer' }
          : null,
      })
    } catch (error: unknown) {
      const statusCode =
        error && typeof error === 'object' && 'statusCode' in error
          ? (error as { statusCode: number }).statusCode
          : 0

      // On 401, try refreshing only if we have a refresh token hint
      if (statusCode === 401 && refreshToken) {
        const refreshed = await get().refreshToken()
        if (refreshed) {
          try {
            const user = await authApi.getMe()
            set({ user, isAuthenticated: true, isLoading: false })
            return
          } catch {
            // Refresh worked but getMe failed
          }
        }
      }

      clearAuthTokens()
      set({ user: null, tokens: null, isAuthenticated: false, isLoading: false })
    }
  },

  updateUser: (updates: Partial<User>) => {
    const current = get().user
    if (current) {
      set({ user: { ...current, ...updates } })
    }
  },

  clearError: () => {
    set({ error: null })
  },
}))
